import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { Button, ButtonOutline } from 'remote-com-shared/components/Button';
import { sharedLinkResolver } from 'remote-com-shared/domains/navigation/hrefResolver';

import MenuEntry from './MenuEntry';
import MenuItem from './MenuItem';
import { SubMenuIconComponent } from './NavigationMenuIcons';
import {
  ClickableSubMenuGroupLabel,
  MenuPanel,
  SubMenu,
  SubMenuEntry,
  SubMenuGroup,
  SubMenuGroupLabel,
  SubMenuGroups,
  SubMenuItem,
  SubMenuText,
} from './styles/Menu';
import { NavigationPrimary, NavigationSecondary } from './styles/Navigation';

function NavigationMenuDropdownSubEntry(entry) {
  const { label, link, iconName } = entry;

  switch (link?.linkType) {
    case 'Link.document':
      return (
        <SubMenuEntry>
          <Link href={sharedLinkResolver(link)} passHref legacyBehavior>
            <SubMenuItem as="a">
              <SubMenuIconComponent name={iconName} />
              <SubMenuText>{label}</SubMenuText>
            </SubMenuItem>
          </Link>
        </SubMenuEntry>
      );
    case 'Link.web': {
      const { url, target } = link;
      return (
        <SubMenuEntry>
          <SubMenuItem as="a" href={url} target={target}>
            <SubMenuIconComponent name={iconName} />
            <SubMenuText>{label}</SubMenuText>
          </SubMenuItem>
        </SubMenuEntry>
      );
    }

    default:
      return null;
  }
}

function SubMenuHeader({ link, label }) {
  return link ? (
    <Link href={sharedLinkResolver(link)} passHref legacyBehavior legacyBehavior>
      <ClickableSubMenuGroupLabel as="a">{label}</ClickableSubMenuGroupLabel>
    </Link>
  ) : (
    <SubMenuGroupLabel>{label}</SubMenuGroupLabel>
  );
}

function SubMenuEntries({ entries }) {
  return (
    <SubMenu>
      {entries.map((entry, index) => (
        <NavigationMenuDropdownSubEntry {...entry} key={index} />
      ))}
    </SubMenu>
  );
}

function NavigationMenuDropdownSlice(entry) {
  const {
    primary: {
      label,
      columnLabel1,
      columnLabel1Link,
      columnLabel2,
      columnLabel2Link,
      columnLabel3,
      columnLabel3Link,
    },
    fields: subMenuEntries,
  } = entry;

  const dropdownUsesColumns = subMenuEntries.some(({ column }) => column !== 'none');
  const column1Entries = subMenuEntries.filter(({ column }) => column === 'column-1');
  const column2Entries = subMenuEntries.filter(({ column }) => column === 'column-2');
  const column3Entries = subMenuEntries.filter(({ column }) => column === 'column-3');
  const dropdownUsesColumn1 = column1Entries.length > 0;
  const dropdownUsesColumn2 = column2Entries.length > 0;
  const dropdownUsesColumn3 = column3Entries.length > 0;

  // Set correct column layout
  let $columns = null;
  if (dropdownUsesColumn2) {
    $columns = '2-wide';
  }
  if (dropdownUsesColumn3) {
    $columns = '3-wide';
  }

  const router = useRouter();

  useEffect(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [router]);

  return (
    <MenuEntry>
      {(isMenuOpen, setIsMenuOpen) => (
        <>
          <MenuItem hasSubMenu isOpen={isMenuOpen} onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {label}
          </MenuItem>

          <MenuPanel $columns={$columns} $isOpen={isMenuOpen} aria-label={label}>
            {dropdownUsesColumns && (
              <SubMenuGroups>
                {dropdownUsesColumn1 && (
                  <SubMenuGroup>
                    <SubMenuHeader link={columnLabel1Link} label={columnLabel1} />
                    <SubMenuEntries
                      entries={subMenuEntries.filter(({ column }) => column === 'column-1')}
                    />
                  </SubMenuGroup>
                )}
                {dropdownUsesColumn2 && (
                  <SubMenuGroup>
                    <SubMenuHeader link={columnLabel2Link} label={columnLabel2} />
                    <SubMenuEntries
                      entries={subMenuEntries.filter(({ column }) => column === 'column-2')}
                    />
                  </SubMenuGroup>
                )}
                {dropdownUsesColumn3 && (
                  <SubMenuGroup>
                    <SubMenuHeader link={columnLabel3Link} label={columnLabel3} />
                    <SubMenuEntries
                      entries={subMenuEntries.filter(({ column }) => column === 'column-3')}
                    />
                  </SubMenuGroup>
                )}
              </SubMenuGroups>
            )}

            {!dropdownUsesColumns && <SubMenuEntries entries={subMenuEntries} />}
          </MenuPanel>
        </>
      )}
    </MenuEntry>
  );
}

function NavigationMenuItemSlice(entry) {
  const { label, link } = entry.primary;

  switch (link?.linkType) {
    case 'Link.document':
      return (
        <MenuEntry>
          <Link href={sharedLinkResolver(link)} passHref legacyBehavior>
            <MenuItem as="a">{label}</MenuItem>
          </Link>
        </MenuEntry>
      );
    case 'Link.web': {
      const { url, target } = link;
      return (
        <MenuEntry>
          <MenuItem as="a" href={url} target={target}>
            {label}
          </MenuItem>
        </MenuEntry>
      );
    }
    default:
      return null;
  }
}

function NavigationMenuCtaButton({ children, variant, ...props }) {
  switch (variant) {
    case 'regular': {
      return (
        <Button tone="primary" shadow {...props}>
          {children}
        </Button>
      );
    }
    case 'outline': {
      return (
        <ButtonOutline tone="primary" {...props}>
          {children}
        </ButtonOutline>
      );
    }
    default:
      return null;
  }
}

function NavigationMenuCtaButtonSlice(entry) {
  const { label, link, variant } = entry.primary;

  switch (link?.linkType) {
    case 'Link.document':
      return (
        <Link href={sharedLinkResolver(link)} passHref legacyBehavior>
          <NavigationMenuCtaButton forwardAs="a" variant={variant} rel="nofollow">
            {label}
          </NavigationMenuCtaButton>
        </Link>
      );
    case 'Link.web': {
      const { url, target } = link;
      return (
        <NavigationMenuCtaButton
          forwardAs="a"
          variant={variant}
          href={url}
          target={target}
          rel="nofollow"
        >
          {label}
        </NavigationMenuCtaButton>
      );
    }
    default:
      return null;
  }
}

export function NavigationMenuPrimary({ entries = [] }) {
  return (
    <>
      {entries.map(({ sliceType, ...entry }, index) => {
        switch (sliceType) {
          case 'header_navigation_dropdown':
            return <NavigationMenuDropdownSlice {...entry} key={index} />;
          case 'header_navigation_item':
            return <NavigationMenuItemSlice {...entry} key={index} />;
          default:
            return null;
        }
      })}
    </>
  );
}

export function NavigationMenuSecondary({ entries = [] }) {
  return (
    <>
      {entries.map(({ sliceType, ...entry }, index) => {
        switch (sliceType) {
          case 'header_navigation_cta_button':
            return <NavigationMenuCtaButtonSlice {...entry} key={index} />;
          default:
            return null;
        }
      })}
    </>
  );
}

export const NavigationMenu = ({ navigationPrimary, navigationSecondary }) => (
  <>
    <NavigationPrimary>
      <NavigationMenuPrimary entries={navigationPrimary} />
    </NavigationPrimary>

    <NavigationSecondary>
      <NavigationMenuSecondary entries={navigationSecondary} />
    </NavigationSecondary>
  </>
);
